import { ReactNode } from "react";
import { SelectOption } from "../../../../types/select";
import { GridType } from "../../../../types/grid";

export const receiptsStatusOptions: SelectOption[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "awaiting_approval" },
  { label: "Bounced", value: "bounced" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Cleared", value: "cleared" },
  { label: "Deferred", value: "deferred" },
  { label: "Incoming Transfer (Bank Transfer)", value: "incoming_transfer" },
  { label: "Not Cleared", value: "paid" },
  // { label: "Paid", value: "paid" },
  { label: "Recalled", value: "recalled" },
];

export type PropertyOption = {
  name: string;
  label: string;
  search?: boolean;
  allowClear?: boolean;
  initialValue: string;
  options: SelectOption[];
  dropdownRender?: (children: ReactNode) => ReactNode;
};

export const defaultSettings = {
  sortable: true,
  resizable: true,
  minWidth: 200,
};

export const getReceiptBulkRowKey = (row: any): string => String(row?.paymentReceiptId ?? row?._id ?? "");

/** Rows eligible for grid header/row checkbox selection (e.g. bulk clear). */

export const isReceiptBulkSelectionRow = (row: any): boolean => {
  if (!row) return false;
  const paymentStatus = typeof row?.paymentStatus === "string" ? row.paymentStatus.trim().toLowerCase() : "";
  const recallStatus = row?.recall?.status;
  return row?.isCleared === false && paymentStatus === "paid" && recallStatus !== "pending";
};

export const normalizeReceiptRow = (row: any): string[] | undefined => {
  const pdc = row?.pdc || {};
  const receiptPaymentReceiptId = row?.paymentReceiptId;
  const receiptDocumentId = row?.receiptDocumentId;
  const totalAmount = row?.totalAmount ?? 0;
  const receiptNo = row?.receiptNo || "";
  const receiptRowStatus = row?.paymentStatus || null;
  const isCleared = row?.isCleared;
  // let chequeDate = pdc?.chequeDate || null;
  const merged: any = {
    ...row,
    ...pdc,
  };

  // Receipt list ids must come from the receipt row, not nested pdc (pdc may carry documentId_*).
  if (receiptPaymentReceiptId) {
    merged.paymentReceiptId = receiptPaymentReceiptId;
  }

  merged.isCleared = isCleared;

  if (receiptRowStatus) {
    merged.paymentStatus = receiptRowStatus;
  }
  if (totalAmount) {
    merged.totalAmount = totalAmount;
  }
  // if (chequeDate) {
  //   merged.chequeDate = chequeDate;
  // }
  if (receiptDocumentId) {
    merged.receiptDocumentId = receiptDocumentId;
  }
  if (receiptNo) {
    merged.receiptNo = receiptNo;
  }

  if (row?.recall?.approvedAt) {
    merged.recallDate = row.recall.approvedAt;
  }
  if (row?.approval != null && typeof row.approval?.status === "string" && row.approval.status.trim() !== "") {
    merged.receiptApprovalRequest = row.approval.status.trim();
  }

  // Receipts screen history should reflect receipt response `statusHistory`.
  if (Array.isArray(row?.statusHistory)) {
    merged.statusHistory = row.statusHistory;
  }

  // Receipt API uses paymentMode/paymentDate/totalAmount; grid expects modeOfPayment/paidDate/actualAmount.
  if (row?.paymentMode) {
    merged.modeOfPayment = row.paymentMode;
  }
  if (!merged.paidDate && row?.paymentDate) {
    merged.paidDate = row.paymentDate;
  }
  if (row?.paymentDate) {
    merged.receiptDate = row.paymentDate;
  }
  if ((merged.actualAmount === undefined || merged.actualAmount === null) && row?.totalAmount !== undefined) {
    merged.actualAmount = row.totalAmount;
  }
  if ((merged.balance === undefined || merged.balance === null) && row?.totalAmount !== undefined) {
    merged.balance = row.totalAmount;
  }
  if (!merged.documentNo) {
    merged.documentNo = row?.paymentDetails?.chequeNo || row?.paymentDetails?.documentNo || "";
  }

  if (!merged.bankName) {
    merged.bankName = row?.paymentDetails?.bankName || row?.bankName || "";
  }
  if (!merged.chequeStatus) {
    merged.chequeStatus = row?.paymentDetails?.status || row?.paymentStatus || row?.status || "";
  }
  if (!merged.receiptStatus) {
    merged.receiptStatus = row?.receiptStatus || "";
  }
  if (!merged.description) {
    merged.description = row?.paymentType || row?.description || "";
  }

  if (row?.invoiceNo && !merged.invoiceNumber) {
    merged.invoiceNumber = row.invoiceNo;
  }

  if (!merged.erpReceiptId) {
    merged.erpReceiptId = row?.erpReceipt?.erpReceiptId || "";
  }
  if (!merged.erpTenantId) {
    merged.erpTenantId = row?.erpReceipt?.erpTenantId || row?.erpTenantId || "";
  }
  if (!merged.erpInvoiceNumber) {
    const invoiceNumbers = row?.erpInvoiceNumber;
    merged.erpInvoiceNumber = Array.isArray(invoiceNumbers)
      ? invoiceNumbers.filter(Boolean).join(", ")
      : invoiceNumbers || "";
  }

  if (row?.recall?.status && !merged.recallChequeRequestStatus) {
    merged.recallChequeRequestStatus = row.recall.status;
  }

  if (row?.property && !merged.propertyDetails?.propertyName) {
    merged.propertyDetails = {
      ...(merged.propertyDetails || {}),
      propertyName: row.property,
    };
  }
  if (row?.propertyName && !merged.propertyDetails?.propertyName) {
    merged.propertyDetails = {
      ...(merged.propertyDetails || {}),
      propertyName: row.propertyName,
    };
  }

  if (row?.tenantName && !merged.tenantDetails?.tenantNameEN) {
    merged.tenantDetails = {
      ...(merged.tenantDetails || {}),
      tenantNameEN: row.tenantName,
    };
  }

  return merged;
};

export const receiptExportColumns = (columns: GridType[]): string[] => {
  return (
    columns.filter((col) => !col.hide && typeof col.headerName === "string").map((col) => col.headerName as string) ||
    []
  );
};
